import { useMemo } from 'react'
import { Button, CircularProgress, Typography } from '@mui/material'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { BulkEditDialogProps } from './BulkEditValueDialog'
import useBulkChangeSet from './useBulkChangeSet'

export default function ClearFieldValuesDialog({
  overlay,
  field,
  selectedRows,
  workflowId,
  workflowStateId,
  listQueryKey,
  setPendingEdits,
}: BulkEditDialogProps) {
  const { fieldsMap } = useProjectContext()

  const { submit, isSaving } = useBulkChangeSet({
    field,
    workflowId,
    workflowStateId,
    listQueryKey,
    setPendingEdits,
    overlay,
    successMessage: (docCount) =>
      `Clearing ${field.name} for ${docCount} document${
        docCount === 1 ? '' : 's'
      }.`,
    errorMessage:
      'Failed to clear some document values. Please try again later.',
  })

  const parentField = fieldsMap[field.id]?.parentField
  const isTableField = !!parentField
  const tableFieldName = parentField?.name ?? ''

  const numberOfDocuments = useMemo(
    () => new Set(selectedRows.map((row) => row.document_id)).size,
    [selectedRows],
  )

  function handleConfirm() {
    submit(selectedRows, {
      manual_value: '',
      manual_ref_id: null,
      manual_value_json: null,
    })
  }

  return (
    <Dialog
      title={`Clear ${field.name}`}
      maxWidth="sm"
      {...overlay}
      onClose={() => overlay.close()}
    >
      <DialogContent>
        <Typography>
          This will set the value of <strong>{field.name}</strong>{' '}
          {isTableField ? (
            <>
              <strong>
                {selectedRows.length} {tableFieldName} row
                {selectedRows.length === 1 ? '' : 's'}
              </strong>{' '}
              across{' '}
              <strong>
                {numberOfDocuments} document
                {numberOfDocuments === 1 ? '' : 's'}
              </strong>
            </>
          ) : (
            <>
              <strong>
                {numberOfDocuments} selected document
                {numberOfDocuments === 1 ? '' : 's'}
              </strong>
            </>
          )}{' '}
          to empty. This action cannot be undone.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This manually sets each field&apos;s value to empty. It does not
          remove the original prediction or any chips—those will still be
          visible, but the saved value will be blank.
        </Typography>
      </DialogContent>
      <DialogFooter>
        <Button
          variant="text"
          onClick={() => overlay.close()}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          Clear All Values
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
